<template>
  <div class="container-fluid font-regular">
    <div class="d-flex flex-wrap align-items-stretch justify-content-between text-center">
      <div class="section-margin col-md-12" v-if="showMainHeader">
        <h1 class="">SMS Segment Calculator</h1>
        <p>Calculate the length of the message segment and the character count of your text message.</p>
      </div>
      <div class="flex-fill text-center section-margin col-md-6">
        <div class="subtitle-text subtitle-text-margin font-bold pull-left">
          <label for="message">Original Message</label>
        </div>
        <textarea v-model="message.body" class="form-control text-monospace p-4 message-container"
                  style="border-radius: 0.625rem;" id="message"></textarea>
        <div class="pull-right">{{ message.bodyLength }}</div>
      </div>
      <div class="flex-fill text-center section-margin col-md-6">
        <div style="padding-bottom: 1.25rem">
          <div class="subtitle-text subtitle-text-margin font-bold pull-left">
            <label for="message">Message Detail</label>
          </div>
          <div class="table-responsive card details-table-border" style="border-radius: 0.625rem;">
            <table class="table overflow-hidden bg-blue-16" style="margin-bottom: 0px;">
              <thead class="">
              <tr class="">
                <th class="mobile-table-header text-white text-center align-middle details-table-border">
                  Segments
                </th>
                <th class="mobile-table-header text-white text-center align-middle details-table-border">
                  Message Length
                </th>
                <th class="mobile-table-header text-white text-center align-middle  details-table-border">
                  Max Segment Length
                </th>
                <th class="mobile-table-header text-white text-center align-middle details-table-border">
                  Encoding Type
                </th>
              </tr>
              </thead>
              <tbody class="">
              <tr class="bg-white">
                <td class="font-weight-bold text-center align-middle details-table-border border rounded-bottom-left">
                  {{ numberOfSegments }}
                </td>
                <td class="font-weight-bold text-center align-middle details-table-border border"
                    style="border-left-width: 2px !important;">
                  {{ numberOfSegments * limitOfCharactersPerSegment }}
                </td>
                <td class="font-weight-bold text-center align-middle details-table-border border "
                    style="border-left-width: 2px !important;">
                  {{ limitOfCharactersPerSegment }}
                </td>
                <td class="font-weight-bold text-center align-middle details-table-border border rounded-bottom-right"
                    style="border-left-width: 2px !important;">
                  {{ message.smsEncodingType }}
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div style="padding-top: 1.25rem; padding-bottom: 1.25rem">
          <div class="subtitle-text subtitle-text-margin font-bold pull-left">
            <label for="message">Price Detail</label>
          </div>
          <div class="table-responsive card details-table-border" style="border-radius: 0.625rem;">
            <table class="table overflow-hidden bg-blue-16" style="margin-bottom: 0px;">
              <thead class="">
              <tr class="">
                <th class="mobile-table-header text-white text-center align-middle details-table-border">
                  iPro
                </th>
                <th class="mobile-table-header text-white text-center align-middle details-table-border">
                  uPro
                </th>
                <th class="mobile-table-header text-white text-center align-middle details-table-border">
                  xPro
                </th>
              </tr>
              </thead>
              <tbody class="">
              <tr class="bg-white">
                <td class="font-weight-bold text-center align-middle details-table-border border rounded-bottom-left">
                 {{ iProTotalPrice}}
                </td>
                <td class="font-weight-bold text-center align-middle details-table-border border"
                    style="border-left-width: 2px !important;">
                 {{ uProTotalPrice}}
                </td>
                <td class="font-weight-bold text-center align-middle details-table-border border rounded-bottom-right"
                    style="border-left-width: 2px !important;">
                 {{ xProTotalPrice}}
                </td>
              </tr>
              </tbody>
            </table>
          </div>
          <div class="footnote">
            *For toll-free, short code, and international numbers, charges are being calculated accordingly.
          </div>
        </div>
        <div style="padding-top: 1.25rem; padding-bottom: 1.25rem; display: flex; justify-content: center; align-items: center;">
          <a :href="startTrialButtonUrl" target="_blank" class="start-trial-button">
            <span>Start free trial</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16">
              <path stroke-linecap="square" stroke-linejoin="bevel" stroke-width="2" d="M2.666 8h9.333M8 3.333 12.667 8 8 12.667"></path>
            </svg>
          </a>
        </div>
      </div>
    </div>
    <div class="flex-fill section-margin col-md-12" v-if="showMessageOutput">
      <div class="flex-1">
        <div class="">
          <div class="subtitle-text font-bold"><label for="message">Encoded Message and Segments</label></div>
          <div class="subtitle-description font-regular">Each segment will be displayed with a different background
            color.
          </div>
        </div>
        <div class="card message-container overflow-auto">
          <div
              :class="['card-body p-4', message.bodyLength === 0 && 'd-flex align-items-center justify-content-center'  ]">
            <p class="text-monospace text-md text-left" v-if="message.bodyLength">
              <template v-for="(character) in message.characters"><span
                  v-if="shouldPrintConcatenationHeaders(character)"
                  :class="['font-weight-bold character-block', getCharacterBackgroundClass(character,true)]">HEADER</span><span
                  :class="['character-block', getCharacterBackgroundClass(character)]">{{
                  character.visibleChar === '\n' || character.visibleChar === '\r\n' ? '&nbsp' : character.visibleChar
                }}</span></template>
            </p>
            <p class="card-text" v-else>
              No message entered
            </p>
          </div>
        </div>
        <div class="pull-right">{{ message.encodedLength }}</div>
      </div>
    </div>
    <div class="flex-fill section-margin col-md-12" v-if="showWarnings && message.hasLineBreaks">
      <div class="flex-fill">
        <span class="text-xl">Warnings</span>
        <p>
          If the message has line breaks, the web page utility only supports LF style.
          If you insert a CRLF it will be converted to LF.
        </p>
      </div>
    </div>
  </div>
</template>

<script>

import {sms_mixin} from "../../mixins";
import * as SMS from "../../constants/sms";

export default {
  name: 'sms-segment-calculator',

  mixins: [sms_mixin],

  data() {
    return {
      SMS: SMS,
      showMessageOutput: true,
      showMessageOutputHeaders: true,
      showWarnings: true,
      showMainHeader: false,
      selectedPosition: null,
      startTrialButtonUrl: 'https://app.aloware.com/signup',
      iProSegmentPrice: 0.02,
      uProSegmentPrice: 0.015,
      xProSegmentPrice: 0.01,
    }
  },
  watch: {
    'message.body': function (message) {
      this.$_sms_mixin_inspectMessage(message)
    }
  },
  computed: {
    iProTotalPrice() {
      return this.calculateTotalPrice(this.iProSegmentPrice);
    },
    uProTotalPrice() {
      return this.calculateTotalPrice(this.uProSegmentPrice);
    },
    xProTotalPrice() {
      return this.calculateTotalPrice(this.xProSegmentPrice);
    }
  },
  methods: {
    /**
     * Calculates the total price based on the segment price and the number of segments.
     *
     * @param {number} rateSegmentPrice - The price per segment.
     * @returns {string} - The total price formatted as a string with a dollar sign and exactly three decimal places.
     *                     If the number of segments is 0, returns '$0'.
     */
    calculateTotalPrice(rateSegmentPrice) {
      if (this.numberOfSegments === 0) {
        return '$' + 0;
      }
      return '$' + (this.numberOfSegments * rateSegmentPrice).toFixed(3);
    },
    /**
     * Returns the background color class based on the position of the character
     * @returns {string}
     * @param character
     * @param isHeader
     */
    getCharacterBackgroundClass(character, isHeader = false) {

      // Define the color classes
      const colorClasses = ['bg-purple', 'bg-yellow', 'bg-green', 'bg-orange', 'bg-blue'];

      // Return the color class based on the segment number
      const colorClass = colorClasses[character.segmentNumber % 5];

      if (isHeader) {
        return `${colorClass}-3`;
      }
      // Return the color class based on the selected position. If the position is hovered, return the darker color class.
      return [SMS.SMS_ENCODING_TYPE_GSM_7_EXTENSION, SMS.SMS_ENCODING_TYPE_UCS_2].includes(character.smsEncodingType) ? `${colorClass}-7` : `${colorClass}-3`;
    },
    shouldPrintConcatenationHeaders(character) {
      if (!this.showMessageOutputHeaders) {
        return false;
      }

      if (this.numberOfSegments <= 1) {
        return false
      }

      return character.decodedPosition === 0 || character.decodedPosition % this.limitOfCharactersPerSegment === 0;
    }
  },
  beforeMount() {
    // checks showMainHeader query parameter
    if (this.$route.query.ShowMainHeader) {
      this.showMainHeader = this.$route.query.ShowMainHeader === 'true';
    }

    // checks ShowMessageOutput query parameter
    if (this.$route.query.ShowMessageOutput) {
      this.showMessageOutput = this.$route.query.ShowMessageOutput === 'true';
    }

    // checks ShowMessageOutputHeaders query parameter
    if (this.$route.query.ShowMessageOutputHeaders) {
      this.showMessageOutputHeaders = this.$route.query.ShowMessageOutputHeaders === 'true';
    }

    // checks ShowWarnings query parameter
    if (this.$route.query.ShowWarnings) {
      this.showWarnings = this.$route.query.ShowWarnings === 'true';
    }

  },

}
</script>
<style scoped>
/* Import Google Fonts - Plus Jakarta Sans */
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');

/* Defines the Message output character block */
.character-block {
  align-items: center;
  color: black;
  font-family: monospace;
  justify-content: center;
  text-align: center;
  vertical-align: middle;
}

.section-margin {
  @media screen and (max-width: 767px) {
    margin-bottom: 40px;
  }
  @media screen and (min-width: 768px) {
    margin-bottom: 10px;
  }
}

@media screen and (max-width: 767px) {
  .mobile-table-header {
    font-size: 3vw;
  }
}

td, th {
  padding: .75rem;
}

.message-container {
  height: 15rem;
}

.overflow-auto {
  overflow: auto;
}

.overflow-hidden {
  overflow: hidden; /* this is because of the rounded corners. If a background color is used, the corners will be visible.*/
}

.details-table-border {
  border-color: rgb(26, 65, 64) !important;
}

.rounded-bottom-right {
  border-bottom-right-radius: 0.625rem !important;
}

.rounded-bottom-left {
  border-bottom-left-radius: 0.625rem !important;
}

.subtitle-text {
  color: black;
  font-size: 1.0625rem; /* 17px / 16 = 1.0625rem */
}

@media screen and (min-width: 768px) {
  .subtitle-text-margin {
    margin-bottom: 1.5625rem
  }
}

.subtitle-description {
  color: black;
  font-size: 1rem; /* 16px */
}

@media screen and (min-width: 768px) {
  .subtitle-description {
    margin-bottom: 1.5625rem
  }
}


.font-regular {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-weight: 400;
  font-style: normal;
}

.font-bold {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-weight: 700;
  font-style: normal;
}

.footnote {
  font-size: 0.75rem;
  color: #6c757d; /* Bootstrap's text-muted color */
  text-align: left;
}

@media screen and (min-width: 768px) {
  .message-container {
    min-height: 313px;
  }
}

/* Start Trial Button Styles */
.start-trial-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 8px 10px 10px 12px;
  background: rgb(208, 242, 122);
  color: rgb(20, 22, 61) !important;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  border: none;
  border-radius: 4px;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0px 0.25rem 0.5625rem rgba(0, 0, 0, 0.05);
}

.start-trial-button:hover {
  background: #65A30D;
  transform: translateY(-1px);
  box-shadow: 0px 0.375rem 0.75rem rgba(0, 0, 0, 0.1);
  color: rgb(255, 255, 255) !important;
  text-decoration: none;
}

.start-trial-button span {
  color: rgb(20, 22, 61) !important;
}

.start-trial-button:hover span {
  color: rgb(255, 255, 255) !important;
}

.start-trial-button:active {
  transform: translateY(0);
}

.start-trial-button svg {
  transition: transform 0.2s ease;
  stroke: rgb(0, 168, 89);
}

.start-trial-button:hover svg {
  transform: translateX(2px);
  stroke: rgb(208, 242, 122);
}

/* Override table background color */
.bg-blue-16 {
  background-color: rgb(26, 65, 64) !important;
}

/* Apply Plus Jakarta Sans font and color to all text elements */
.container-fluid,
.container-fluid h1,
.container-fluid p,
.container-fluid label,
.container-fluid td,
.container-fluid th,
.container-fluid div,
.container-fluid span,
.container-fluid .footnote,
.container-fluid .card-text {
  font-family: 'Plus Jakarta Sans', sans-serif !important;
  color: rgb(91, 92, 119) !important;
}

/* Keep specific styles for certain elements */
.container-fluid .text-white {
  color: white !important;
}
</style>
<style>
.user-app-body {
  background-color: transparent !important;
}
</style>
